// src/components/admin/AnalyticsDashboard.tsx
// Dashboard analitik traffic pengunjung dengan rincian total kunjungan, pengunjung unik, dan filter per bulan

import React, { useState, useEffect } from 'react';
import { useAnalyticsData, TrafficOverviewSummary, MonthlyStatsItem } from '../../hooks/useAnalytics';
import {
  Calendar,
  Users,
  Eye,
  Heart,
  TrendingUp,
  Monitor,
  Smartphone,
  Globe,
  RefreshCw,
  ChevronDown,
  BarChart3,
  CalendarDays,
} from 'lucide-react';

export const AnalyticsDashboard: React.FC = () => {
  const { fetchMonthlyTraffic } = useAnalyticsData();
  const [data, setData] = useState<TrafficOverviewSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonthKey, setSelectedMonthKey] = useState<string>('all');

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await fetchMonthlyTraffic();
      setData(result);
      if (result.monthlyBreakdown.length > 0 && selectedMonthKey === 'all') {
        // Default ke 'all' atau bulan terbaru
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [fetchMonthlyTraffic]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3"></div>
        <p className="text-sm text-gray-500">Memuat data traffic & pengunjung...</p>
      </div>
    );
  }

  if (!data) return null;

  // Filter bulan yang sedang aktif
  const currentSelectedMonth: MonthlyStatsItem | undefined =
    selectedMonthKey !== 'all'
      ? data.monthlyBreakdown.find((m) => m.monthKey === selectedMonthKey)
      : undefined;

  // Nilai yang ditampilkan pada kartu metrik utama
  const displayViews = currentSelectedMonth ? currentSelectedMonth.totalViews : data.allTimeViews;
  const displayVisitors = currentSelectedMonth
    ? currentSelectedMonth.uniqueVisitors
    : data.allTimeUniqueVisitors;
  const periodLabel = currentSelectedMonth ? currentSelectedMonth.label : 'Semua Waktu';

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 sm:p-6 rounded-2xl border border-gray-200 shadow-xs">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
              <TrendingUp className="h-5 w-5" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Laporan Traffic & Pengunjung
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            Pantau frekuensi kunjungan website, pengunjung unik per device, dan perbandingan tiap bulan.
          </p>
        </div>

        {/* Filter Bulan & Refresh */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={selectedMonthKey}
              onChange={(e) => setSelectedMonthKey(e.target.value)}
              className="appearance-none bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-800 text-xs sm:text-sm font-medium rounded-xl py-2 pl-3 pr-8 focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
            >
              <option value="all">📅 Semua Waktu (Akumulatif)</option>
              {data.monthlyBreakdown.map((m) => (
                <option key={m.monthKey} value={m.monthKey}>
                  Bulan {m.label} ({m.totalViews} views)
                </option>
              ))}
            </select>
            <ChevronDown className="h-4 w-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          <button
            onClick={loadData}
            title="Refresh Data"
            className="p-2 rounded-xl bg-gray-50 hover:bg-gray-100 text-gray-600 border border-gray-200 cursor-pointer active:scale-95 transition-all"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* 4 Kartu Metrik Utama */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {/* Total Page Views */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Total Kunjungan
            </span>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
              <Eye className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-3xl font-extrabold text-gray-900">
              {displayViews.toLocaleString()}
            </div>
            <div className="text-xs text-blue-600 font-medium mt-1 flex items-center gap-1">
              <span>{periodLabel}</span>
            </div>
          </div>
        </div>

        {/* Pengunjung Unik */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Pengunjung Unik
            </span>
            <div className="p-2 bg-purple-50 text-purple-600 rounded-xl">
              <Users className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-3xl font-extrabold text-gray-900">
              {displayVisitors.toLocaleString()}
            </div>
            <div className="text-xs text-purple-600 font-medium mt-1 flex items-center gap-1">
              <span>Device / Browser unik</span>
            </div>
          </div>
        </div>

        {/* Kunjungan Bulan Berjalan */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Bulan Berjalan
            </span>
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
              <Calendar className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-3xl font-extrabold text-gray-900">
              {data.thisMonthViews.toLocaleString()}
            </div>
            <div className="text-xs text-emerald-600 font-medium mt-1">
              {data.thisMonthUniqueVisitors.toLocaleString()} pengunjung unik bulan ini
            </div>
          </div>
        </div>

        {/* Total Interaksi Likes */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Dukungan (Likes)
            </span>
            <div className="p-2 bg-rose-50 text-rose-600 rounded-xl">
              <Heart className="h-5 w-5 fill-rose-500 text-rose-500" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-3xl font-extrabold text-gray-900">
              {data.totalLikes.toLocaleString()}
            </div>
            <div className="text-xs text-rose-600 font-medium mt-1">
              Interaksi suka dari pengunjung
            </div>
          </div>
        </div>
      </div>

      {/* Tabel Rincian Tiap Bulan (Monthly Breakdown) */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xs overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            <h3 className="font-bold text-gray-900 text-base">
              Riwayat Kunjungan Tiap Bulan
            </h3>
          </div>
          <span className="text-xs text-gray-400">
            {data.monthlyBreakdown.length} bulan tercatat
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50/80 text-gray-500 text-xs font-semibold uppercase tracking-wider border-b border-gray-100">
                <th className="py-3 px-5">Bulan</th>
                <th className="py-3 px-5 text-right">Total Kunjungan</th>
                <th className="py-3 px-5 text-right">Pengunjung Unik</th>
                <th className="py-3 px-5 text-center">Status</th>
                <th className="py-3 px-5 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.monthlyBreakdown.map((m) => {
                const isSelected = selectedMonthKey === m.monthKey;
                return (
                  <tr
                    key={m.monthKey}
                    className={`hover:bg-blue-50/40 transition-colors ${
                      isSelected ? 'bg-blue-50/60 font-semibold text-blue-900' : 'text-gray-700'
                    }`}
                  >
                    <td className="py-3.5 px-5 flex items-center gap-2.5">
                      <CalendarDays className="h-4 w-4 text-gray-400" />
                      <span>{m.label}</span>
                    </td>
                    <td className="py-3.5 px-5 text-right font-bold">
                      {m.totalViews.toLocaleString()}
                    </td>
                    <td className="py-3.5 px-5 text-right font-medium text-purple-700">
                      {m.uniqueVisitors.toLocaleString()}
                    </td>
                    <td className="py-3.5 px-5 text-center">
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                        Aktif
                      </span>
                    </td>
                    <td className="py-3.5 px-5 text-right">
                      <button
                        onClick={() => setSelectedMonthKey(isSelected ? 'all' : m.monthKey)}
                        className={`text-xs px-3 py-1.5 rounded-lg border font-medium cursor-pointer transition-all ${
                          isSelected
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200'
                        }`}
                      >
                        {isSelected ? 'Tutup Rincian' : 'Lihat Rincian'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rincian Harian Pada Bulan yang Dipilih */}
      {currentSelectedMonth && currentSelectedMonth.days.length > 0 && (
        <div className="bg-white rounded-2xl border border-blue-200 shadow-sm p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <div>
              <h3 className="font-bold text-gray-900 text-base">
                Rincian Harian: {currentSelectedMonth.label}
              </h3>
              <p className="text-xs text-gray-500">
                Data kunjungan harian pada bulan {currentSelectedMonth.label}
              </p>
            </div>
            <button
              onClick={() => setSelectedMonthKey('all')}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
            >
              Kembali ke Semua Waktu
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {currentSelectedMonth.days.map((d) => (
              <div
                key={d.date}
                className="p-3 bg-gray-50 rounded-xl border border-gray-200/80 text-center hover:bg-white hover:shadow-xs transition-all"
              >
                <div className="text-xs text-gray-500 font-medium">{d.displayDate}</div>
                <div className="text-xl font-bold text-gray-900 mt-1">
                  {d.views.toLocaleString()}
                </div>
                <div className="text-[11px] text-purple-600 mt-0.5">
                  {d.uniqueVisitors} unik
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bagian Bawah: Halaman Populer & Perangkat */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Halaman Terpopuler */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <h3 className="font-bold text-gray-900 text-sm sm:text-base flex items-center gap-2">
              <Globe className="h-4 w-4 text-blue-600" />
              Halaman Terpopuler
            </h3>
            <span className="text-xs text-gray-400">Paling sering dilihat</span>
          </div>

          <div className="space-y-2.5">
            {data.topPages.map((page, idx) => (
              <div
                key={page.path}
                className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50 text-xs sm:text-sm"
              >
                <div className="flex items-center gap-2.5 truncate">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                    {idx + 1}
                  </span>
                  <span className="font-medium text-gray-800 truncate">
                    {page.path === '/'
                      ? 'Beranda (Landing Page)'
                      : page.path === '/profile'
                      ? 'Halaman Profil Lengkap'
                      : page.path === '/contact'
                      ? 'Halaman Kontak'
                      : page.path}
                  </span>
                </div>
                <span className="font-bold text-blue-600 flex-shrink-0 ml-2">
                  {page.views.toLocaleString()} views
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Perangkat Pengunjung */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <h3 className="font-bold text-gray-900 text-sm sm:text-base flex items-center gap-2">
              <Monitor className="h-4 w-4 text-indigo-600" />
              Perangkat Pengunjung
            </h3>
            <span className="text-xs text-gray-400">Device category</span>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-center">
              <Smartphone className="h-6 w-6 text-blue-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">
                {data.deviceBreakdown.mobile || 1}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">Smartphone / HP</div>
            </div>

            <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-center">
              <Monitor className="h-6 w-6 text-purple-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">
                {data.deviceBreakdown.desktop || 1}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">Desktop / Laptop</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
